import { useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, List, ListItem,
  ListItemText, Paper, Stack, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useNavigate } from "react-router-dom";

interface Scenario {
  id: string; title: string; role: string; talkTrack: string;
  steps: string[]; ucIds: string[]; startPath: string;
}

const SCENARIOS: Scenario[] = [
  {
    id: "1", title: "New bill / sale", role: "Pharmacist",
    talkTrack:
      "Counter staff opens OP billing, scans or searches an item, posts a mixed-tender invoice. " +
      "If the requested Rx is OOS, the substitute panel surfaces formulary alternates.",
    steps: [
      "Click Search & Add — picker opens (stockPolicy=issue)",
      "Tap Paracetamol 500 (in stock) — line appears in cart",
      "Tap Amoxicillin 250 (OOS) — substitute panel offers Amoxicillin 500",
      "Add substitute Amoxicillin 500 — line shows 'Substituted for Amoxicillin 250'",
      "Fill ₹balance on Cash + add Card line — Change-to-return chip turns green when over-tender",
      "Submit (reserves stock) then Post (consumes reservation + ledger ISSUE + balanced JE)",
    ],
    ucIds: ["UC-M1-SAL-02", "UC-M1-SAL-03", "UC-M1-SAL-04"], startPath: "/sales/invoices/new",
  },
  {
    id: "2", title: "Import rate master (effective dates)", role: "Branch Admin",
    talkTrack: "Bulk update of vendor rates by uploading the rate_master CSV — lands in S5b.",
    steps: ["Open rate master upload (placeholder)", "Pick CSV + effective date", "Review preview + commit"],
    ucIds: ["UC-FN-PRC-01"], startPath: "/catalog/items",
  },
  {
    id: "3", title: "Import offers", role: "Branch Admin",
    talkTrack: "Upload of seasonal offer file (Monsoon 5% off Paracetamol etc.). Lands in S5b.",
    steps: ["Open offers upload (placeholder)", "Pick CSV", "Review + commit; offers appear as billing banner"],
    ucIds: ["UC-M1-SAL-05"], startPath: "/catalog/items",
  },
  {
    id: "4", title: "Reorder levels / OOS → Create PO", role: "Store Manager",
    talkTrack:
      "Store Manager checks reorder queue, multi-selects OOS and low-stock items, chooses Last-Supplier " +
      "strategy, and the system drafts one PO per vendor.",
    steps: [
      "Open Reorder queue — items below ROP appear with OOS / Low chips",
      "Select 5 items + pick 'Last supplier' strategy",
      "Click Generate POs — modal lists 1 PO per vendor with line count + total",
      "Open one PO from the modal — Source banner reads 'Generated from reorder alert'",
    ],
    ucIds: ["UC-M1-INV-06", "UC-M1-PROC-02"], startPath: "/procurement/reorder-queue",
  },
  {
    id: "5", title: "Goods receipt (GRN)", role: "Store Manager",
    talkTrack: "Receive against an approved PO, post — ledger RECEIPT rows + moving-avg cost update.",
    steps: [
      "Open Purchase Orders list, click an Approved PO",
      "Click Create GRN — pre-fills lines from PO",
      "Enter batch + expiry, accepted qty per line",
      "Save then open GRN detail, click Post — over-receipt > 10% requires explicit acknowledge",
    ],
    ucIds: ["UC-M1-PROC-04"], startPath: "/procurement/grns/new",
  },
  {
    id: "6", title: "Inventory dashboard", role: "Store Manager",
    talkTrack: "Single view of stock health: low stock, near expiry, in-transit, write-offs.",
    steps: [
      "Open Stock on hand report",
      "Open Near expiry queue and toggle tabs 30 / 60 / 90 days",
      "Open Internal transfers list to see in-transit",
      "Open Stock takes list to inspect cycle counts",
    ],
    ucIds: ["UC-M1-INV-02", "UC-M1-INV-09"], startPath: "/reports/stock-on-hand",
  },
];

/**
 * Demo guide (mockup v10 parity): six collapsible scenarios with talk track + steps + Start →.
 * Used for stakeholder demos and UAT walkthroughs.
 */
export function DemoGuidePage() {
  const navigate = useNavigate();
  const [running, setRunning] = useState<Scenario | null>(null);

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Demo guide</Typography>
      <Paper sx={{ p: 2 }}>
        {SCENARIOS.map(s => (
          <Accordion key={s.id} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                <Chip size="small" label={`#${s.id}`} />
                <Typography sx={{ flex: 1 }}>{s.title}</Typography>
                <Chip size="small" variant="outlined" label={s.role} />
                {s.ucIds.map(uc => (
                  <Chip key={uc} size="small" variant="outlined" color="primary" label={uc} />
                ))}
                <Button
                  size="small" variant="contained" startIcon={<PlayArrowIcon />}
                  onClick={(e) => { e.stopPropagation(); setRunning(s); navigate(s.startPath); }}
                >
                  Start demo →
                </Button>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{s.talkTrack}</Typography>
              <List dense>
                {s.steps.map((step, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={`${i + 1}. ${step}`} />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
      {running && (
        <Box sx={{
          position: "fixed", left: 0, right: 0, bottom: 0,
          bgcolor: "secondary.main", color: "secondary.contrastText", p: 1, zIndex: 1300,
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography sx={{ flex: 1 }}>
              Demo in progress: <b>{running.title}</b> ({running.role})
            </Typography>
            <Button size="small" color="inherit" onClick={() => setRunning(null)}>
              Return to guide
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
